import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  FlaskConical, Save, ChevronLeft, Loader2, CheckCircle2, PlayCircle,
  ClipboardCheck, ShieldCheck, FileText, Archive as ArchiveIcon, Printer,
} from "lucide-react";
import { StatusBadge } from "@/pages/BatchLogs";

const API = process.env.REACT_APP_BACKEND_URL;
const pdfUrl = (id) => {
  const token = localStorage.getItem("access_token") || "";
  return `${API}/api/batch-logs/${id}/pdf${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
};

const STAGE_ORDER = ["draft", "in_progress", "post_fill", "completed"];

export default function BatchLogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null); // {to}
  const [inventory, setInventory] = useState([]);

  const reload = async () => {
    try {
      const { data } = await api.get(`/batch-logs/${id}`);
      setB(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load batch");
      navigate("/batch-logs");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get("/inventory").then((r) => setInventory(r.data.items || [])).catch(() => {});
  }, []);

  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));
  const setIng = (i, k, v) => setB((p) => {
    const ings = [...(p.ingredients || [])];
    ings[i] = { ...ings[i], [k]: v };
    return { ...p, ingredients: ings };
  });
  const setEq = (i, k, v) => setB((p) => {
    const eq = [...(p.equipment || [])];
    eq[i] = { ...eq[i], [k]: v };
    return { ...p, equipment: eq };
  });
  const setVi = (k, v) => setB((p) => ({ ...p, visual_inspection: { ...(p.visual_inspection || {}), [k]: v } }));
  const setDefect = (k, v) => setB((p) => ({
    ...p,
    visual_inspection: {
      ...(p.visual_inspection || {}),
      critical_defects: { ...((p.visual_inspection || {}).critical_defects || {}), [k]: v },
    },
  }));
  const setCsp = (k, v) => setB((p) => ({ ...p, csp_test_results: { ...(p.csp_test_results || {}), [k]: v } }));
  const setCspNested = (branch, k, v) => setB((p) => ({
    ...p,
    csp_test_results: {
      ...(p.csp_test_results || {}),
      [branch]: { ...((p.csp_test_results || {})[branch] || {}), [k]: v },
    },
  }));
  const setFr = (k, v) => setB((p) => ({ ...p, final_release: { ...(p.final_release || {}), [k]: v } }));
  const setVialLot = (i, k, v) => setB((p) => {
    const arr = [...(p.vial_lots || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...p, vial_lots: arr };
  });
  const setDisinfLot = (i, k, v) => setB((p) => {
    const arr = [...(p.disinfection_lots || [])];
    arr[i] = { ...arr[i], [k]: v };
    return { ...p, disinfection_lots: arr };
  });

  const readonly = !isAdmin || b?.status === "completed" || b?.status === "archived";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        lot_number: b.lot_number, planned_start: b.planned_start,
        actual_start: b.actual_start, actual_end: b.actual_end,
        expiration_date: b.expiration_date, notes: b.notes,
        yield_actual: b.yield_actual,
        ingredients: b.ingredients, equipment: b.equipment,
        instructions: b.instructions, visual_inspection: b.visual_inspection,
        // Extended QC (optional — blank values still print blank underscores on PDF)
        ph_reading: b.ph_reading, ph_pass: b.ph_pass, ph_calibration: b.ph_calibration,
        filter_bubble_point_actual: b.filter_bubble_point_actual,
        fill_volume_ml: b.fill_volume_ml, total_discarded_ml: b.total_discarded_ml,
        csp_test_results: b.csp_test_results, final_release: b.final_release,
        vial_lots: b.vial_lots, disinfection_lots: b.disinfection_lots,
      };
      const { data } = await api.put(`/batch-logs/${id}`, payload);
      setB(data);
      toast.success("Batch saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const archive = async () => {
    if (!confirm("Archive this batch? It stays in history but hides from active lists.")) return;
    try {
      await api.delete(`/batch-logs/${id}`);
      toast.success("Archived");      navigate("/batch-logs");
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };

  const printAndComplete = async () => {
    if (!confirm(
      "This will save any pending edits, advance the batch straight to Completed, and open the printable worksheet.\n\n" +
      "Signatures and any remaining data are written by hand on the printed sheet. Continue?"
    )) return;
    setSaving(true);
    try {
      // 1. Persist whatever's on screen so it prints correctly.
      const payload = {
        lot_number: b.lot_number, planned_start: b.planned_start,
        actual_start: b.actual_start, actual_end: b.actual_end,
        expiration_date: b.expiration_date, notes: b.notes,
        yield_actual: b.yield_actual,
        ingredients: b.ingredients, equipment: b.equipment,
        instructions: b.instructions, visual_inspection: b.visual_inspection,
        ph_reading: b.ph_reading, ph_pass: b.ph_pass, ph_calibration: b.ph_calibration,
        filter_bubble_point_actual: b.filter_bubble_point_actual,
        fill_volume_ml: b.fill_volume_ml, total_discarded_ml: b.total_discarded_ml,
        csp_test_results: b.csp_test_results, final_release: b.final_release,
        vial_lots: b.vial_lots, disinfection_lots: b.disinfection_lots,
      };
      await api.put(`/batch-logs/${id}`, payload);

      // 2. Fire every transition needed to land at "completed".
      const remaining = STAGE_ORDER.slice(STAGE_ORDER.indexOf(b.status) + 1);
      for (const step of remaining) {
        await api.post(`/batch-logs/${id}/status`, {
          to: step,
          notes: step === "completed" ? "print-and-complete · signatures hand-written" : "",
        });
      }

      // 3. Refresh state + open the PDF in a new tab for printing.
      const { data } = await api.get(`/batch-logs/${id}`);
      setB(data);
      window.open(pdfUrl(id), "_blank");
      toast.success(`Batch completed · worksheet opened for printing`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Print & complete failed");
    } finally { setSaving(false); }
  };

  const stageIdx = STAGE_ORDER.indexOf(b?.status || "draft");
  const nextStage = useMemo(() => {
    if (!b) return null;
    if (b.status === "draft") return "in_progress";
    if (b.status === "in_progress") return "post_fill";
    if (b.status === "post_fill") return "completed";
    return null;
  }, [b]);

  if (loading) return (
    <div className="p-6 flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (!b) return null;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link to="/batch-logs" className="text-xs text-slate-500 hover:text-pink-700 inline-flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> Back to Batch Log
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
            <FlaskConical className="w-6 h-6 text-pink-700" />
            <span className="font-mono">{b.batch_number}</span>
            <StatusBadge status={b.status} className="text-xs" />
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            {b.formula_name} · Batch amount <b>{b.total_quantity} {b.total_quantity_unit}</b> ·
            planned {b.planned_start} · exp {b.expiration_date || "—"}
          </p>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
            data-testid="batch-pdf-btn"
            onClick={() => window.open(pdfUrl(id), "_blank")}>
            <FileText className="w-3.5 h-3.5" /> PDF
          </Button>
          {isAdmin && b.status !== "completed" && b.status !== "archived" && (
            <>
              <Button data-testid="batch-save-btn" onClick={save} disabled={saving}
                size="sm" className="h-8 gap-1 text-xs bg-pink-700 hover:bg-pink-800">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </Button>
              <Button data-testid="batch-print-complete-btn" onClick={printAndComplete}
                disabled={saving} size="sm"
                className="h-8 gap-1 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                title="Save pending edits, advance the batch straight to Completed, and open the printable worksheet for hand-written entry.">
                <Printer className="w-3.5 h-3.5" /> Print & Complete
              </Button>
              {nextStage && (
                <Button data-testid={`batch-advance-${nextStage}`} onClick={() => setStatusDialog({ to: nextStage })}
                  size="sm" className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                  {nextStage === "in_progress" && <><PlayCircle className="w-3.5 h-3.5" /> Start compounding</>}
                  {nextStage === "post_fill" && <><ClipboardCheck className="w-3.5 h-3.5" /> Mark filled</>}
                  {nextStage === "completed" && <><ShieldCheck className="w-3.5 h-3.5" /> Complete batch</>}
                </Button>
              )}
            </>
          )}
          {isAdmin && b.status !== "archived" && (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-slate-500 hover:text-rose-700"
              data-testid="batch-archive-btn" onClick={archive}>
              <ArchiveIcon className="w-3.5 h-3.5" /> Archive
            </Button>
          )}
        </div>
      </div>

      {/* Stage stepper */}
      <Card className="p-3 mb-4 border-slate-200">
        <div className="flex items-center justify-between">
          {STAGE_ORDER.map((s, i) => {
            const done = i < stageIdx;
            const active = i === stageIdx;
            return (
              <div key={s} className="flex-1 flex items-center gap-2" data-testid={`stage-${s}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold
                  ${done ? "bg-emerald-600 text-white" : active ? "bg-pink-700 text-white" : "bg-slate-100 text-slate-400"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <div className="text-xs">
                  <div className={`font-semibold ${active ? "text-slate-900" : "text-slate-500"}`}>
                    {s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </div>
                </div>
                {i < STAGE_ORDER.length - 1 && (
                  <div className={`flex-1 h-0.5 ${done ? "bg-emerald-600" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Metadata */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Batch metadata</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Lot number" value={b.lot_number} onChange={(v) => set("lot_number", v)} readonly={readonly} mono />
          <Field label="Planned start" value={b.planned_start} onChange={(v) => set("planned_start", v)} readonly={readonly} type="date" />
          <Field label="Expiration date" value={b.expiration_date} onChange={(v) => set("expiration_date", v)} readonly={readonly} type="date" />
          <Field label="Actual start" value={(b.actual_start || "").slice(0, 16)} readonly type="text" />
          <Field label="Actual end" value={(b.actual_end || "").slice(0, 16)} readonly type="text" />
          <Field label="Yield actual" value={b.yield_actual ?? ""} onChange={(v) => set("yield_actual", v === "" ? null : Number(v))} readonly={readonly} type="number" />
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          BUD rule: <b>{(b.formula_snapshot || {}).bud_rule || "—"}</b> · Storage: {(b.formula_snapshot || {}).storage_conditions || "—"} · Expected yield {b.yield_expected} {b.yield_unit}
        </div>
      </Card>

      {/* Ingredients weighed */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Ingredients weighed</h3>
        <div className="text-[11px] text-slate-500 mb-2">
          Pin the exact SKU + lot from Inventory to guarantee accurate drawdown at Post-Fill (safer than name matching, especially for controlled substances).
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Ingredient</TableHead>
                <TableHead className="text-right">Planned / Actual</TableHead>
                <TableHead>Linked SKU · NDC · Manufacturer</TableHead>
                <TableHead>Lot used · exp · on-hand</TableHead>
                <TableHead>Weighed by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(b.ingredients || []).map((ing, i) => (
                <IngredientRow key={i} idx={i} ing={ing} setIng={setIng}
                  inventory={inventory} readonly={readonly} />
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Equipment */}
      {(b.equipment || []).length > 0 && (
        <Card className="p-4 mb-4 border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Equipment checklist</h3>
          <div className="space-y-2">
            {b.equipment.map((e, i) => (
              <label key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!e.checked}
                  disabled={readonly} onChange={(ev) => setEq(i, "checked", ev.target.checked)}
                  data-testid={`batch-eq-${i}`} className="w-4 h-4 accent-pink-700" />
                <span className={e.checked ? "line-through text-slate-400" : ""}>{e.name}</span>
              </label>
            ))}
          </div>
        </Card>
      )}

      {/* Instructions */}
      {(b.instructions || []).length > 0 && (
        <Card className="p-4 mb-4 border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Compounding instructions</h3>
          <ol className="space-y-1.5 list-decimal list-inside text-sm text-slate-700">
            {b.instructions.map((s, i) => (
              <li key={i} data-testid={`batch-step-${i}`}>{s}</li>
            ))}
          </ol>
        </Card>
      )}

      {/* Visual inspection */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Visual inspection</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {["Pass", "Fail", "Not yet"].map((label, idx) => {
            const val = idx === 0 ? true : idx === 1 ? false : null;
            const current = (b.visual_inspection || {}).passed;
            const active = current === val;
            return (
              <button key={label} onClick={() => !readonly && setVi("passed", val)}
                data-testid={`vi-${label.toLowerCase().replace(" ", "-")}`}
                disabled={readonly}
                className={`text-xs px-3 py-1 rounded-full border font-medium ${
                  active ? (val === true ? "bg-emerald-600 border-emerald-600 text-white"
                            : val === false ? "bg-rose-600 border-rose-600 text-white"
                            : "bg-slate-600 border-slate-600 text-white")
                         : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                } disabled:opacity-60`}>{label}</button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Units inspected</label>
            <Input data-testid="vi-count" type="number" value={(b.visual_inspection || {}).count_verified || 0}
              onChange={(e) => setVi("count_verified", Number(e.target.value))} disabled={readonly}
              className="h-9 text-sm font-mono" />
            <p className="text-[11px] text-slate-500 mt-1">Formula requires {(b.formula_snapshot || {}).visual_inspection_count || 0}</p>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Notes</label>
            <Input data-testid="vi-notes" value={(b.visual_inspection || {}).notes || ""}
              onChange={(e) => setVi("notes", e.target.value)} disabled={readonly} className="h-9 text-sm" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Description (optional — prints on PDF)</label>
            <Input data-testid="vi-description" value={(b.visual_inspection || {}).description || ""}
              onChange={(e) => setVi("description", e.target.value)} disabled={readonly}
              placeholder="e.g. Clear solution, no particulates" className="h-9 text-sm" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[10px] text-slate-500 uppercase">Critical defects check (tick any observed — leave clear to print blank checkboxes)</label>
          <div className="flex flex-wrap gap-3 mt-1">
            {[
              ["particulates", "Particulates"],
              ["precipitate", "Precipitate"],
              ["discoloration", "Discoloration"],
              ["container_closure", "Container closure"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs text-slate-700">
                <input type="checkbox" data-testid={`vi-defect-${k}`}
                  checked={!!((b.visual_inspection || {}).critical_defects || {})[k]}
                  disabled={readonly}
                  onChange={(e) => setDefect(k, e.target.checked)}
                  className="w-4 h-4 accent-rose-600" />
                {label}
              </label>
            ))}
          </div>
        </div>
      </Card>

      {/* Extended QC (optional digital entry — blank fields still print underscores on PDF) */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Extended QC · optional digital entry</h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Compounders can hand-write these on the printed worksheet. Any values typed here
          will pre-fill the PDF; leave blank to print underscore lines for ink entry.
        </p>

        {/* pH testing */}
        <div className="mb-4">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">pH testing &amp; adjustment</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Actual pH reading" value={b.ph_reading}
              onChange={(v) => set("ph_reading", v)} readonly={readonly} />
            <Field label="pH calibration (buffer / std)" value={b.ph_calibration}
              onChange={(v) => set("ph_calibration", v)} readonly={readonly} />
            <div>
              <label className="text-[10px] text-slate-500 uppercase">pH Pass?</label>
              <div className="flex gap-1.5 mt-1">
                {[["Pass", true], ["Fail", false], ["—", null]].map(([label, val]) => {
                  const active = b.ph_pass === val;
                  return (
                    <button key={label} type="button" onClick={() => !readonly && set("ph_pass", val)}
                      disabled={readonly} data-testid={`ph-pass-${String(label).toLowerCase()}`}
                      className={`text-xs px-3 py-1 rounded-full border font-medium disabled:opacity-60 ${
                        active
                          ? val === true ? "bg-emerald-600 border-emerald-600 text-white"
                            : val === false ? "bg-rose-600 border-rose-600 text-white"
                            : "bg-slate-600 border-slate-600 text-white"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}>{label}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Filter + yield fills */}
        <div className="mb-4">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">Filter bubble point &amp; yield fills</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Actual bubble point (psi)" value={b.filter_bubble_point_actual}
              onChange={(v) => set("filter_bubble_point_actual", v)} readonly={readonly} type="number" />
            <Field label="Fill volume (mL)" value={b.fill_volume_ml}
              onChange={(v) => set("fill_volume_ml", v)} readonly={readonly} type="number" />
            <Field label="Total discarded (mL)" value={b.total_discarded_ml}
              onChange={(v) => set("total_discarded_ml", v)} readonly={readonly} type="number" />
          </div>
        </div>

        {/* CSP test results */}
        <div className="mb-4">
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">CSP test results</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Primary lab" value={(b.csp_test_results || {}).primary_lab}
              onChange={(v) => setCsp("primary_lab", v)} readonly={readonly} />
            <Field label="Secondary lab" value={(b.csp_test_results || {}).secondary_lab}
              onChange={(v) => setCsp("secondary_lab", v)} readonly={readonly} />
          </div>
          <div className="flex flex-wrap gap-4 mt-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" data-testid="csp-coa" disabled={readonly}
                checked={!!(b.csp_test_results || {}).coa_attached}
                onChange={(e) => setCsp("coa_attached", e.target.checked)}
                className="w-4 h-4 accent-pink-700" />
              COA attached
            </label>
            <label className="flex items-center gap-1.5 text-xs text-slate-700">
              <input type="checkbox" data-testid="csp-analytical" disabled={readonly}
                checked={!!(b.csp_test_results || {}).analytical_testing}
                onChange={(e) => setCsp("analytical_testing", e.target.checked)}
                className="w-4 h-4 accent-pink-700" />
              Analytical testing performed
            </label>
          </div>

          {/* Sterility */}
          <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">Sterility test</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Vials sent" value={(b.csp_test_results || {}).sterility?.vials_sent ?? ""}
                onChange={(v) => setCspNested("sterility", "vials_sent", v === "" ? null : Number(v))}
                readonly={readonly} type="number" />
              <Field label="Method" value={(b.csp_test_results || {}).sterility?.method}
                onChange={(v) => setCspNested("sterility", "method", v)} readonly={readonly} />
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Result</label>
                <div className="flex gap-1.5 mt-1">
                  {[["Pass", true], ["Fail", false], ["Pending", null]].map(([label, val]) => {
                    const active = ((b.csp_test_results || {}).sterility || {}).passed === val;
                    return (
                      <button key={label} type="button" data-testid={`ster-${String(label).toLowerCase()}`}
                        onClick={() => !readonly && setCspNested("sterility", "passed", val)}
                        disabled={readonly}
                        className={`text-xs px-3 py-1 rounded-full border font-medium disabled:opacity-60 ${
                          active
                            ? val === true ? "bg-emerald-600 border-emerald-600 text-white"
                              : val === false ? "bg-rose-600 border-rose-600 text-white"
                              : "bg-slate-600 border-slate-600 text-white"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>{label}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Endotoxin */}
          <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">Endotoxin test</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Vials sent" value={(b.csp_test_results || {}).endotoxin?.vials_sent ?? ""}
                onChange={(v) => setCspNested("endotoxin", "vials_sent", v === "" ? null : Number(v))}
                readonly={readonly} type="number" />
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Result</label>
                <div className="flex gap-1.5 mt-1">
                  {[["Pass", true], ["Fail", false], ["Pending", null]].map(([label, val]) => {
                    const active = ((b.csp_test_results || {}).endotoxin || {}).passed === val;
                    return (
                      <button key={label} type="button" data-testid={`endo-${String(label).toLowerCase()}`}
                        onClick={() => !readonly && setCspNested("endotoxin", "passed", val)}
                        disabled={readonly}
                        className={`text-xs px-3 py-1 rounded-full border font-medium disabled:opacity-60 ${
                          active
                            ? val === true ? "bg-emerald-600 border-emerald-600 text-white"
                              : val === false ? "bg-rose-600 border-rose-600 text-white"
                              : "bg-slate-600 border-slate-600 text-white"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>{label}</button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final release */}
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1.5">Final release</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Final release date" value={(b.final_release || {}).date}
              onChange={(v) => setFr("date", v)} readonly={readonly} type="date" />
            <Field label="Checked by" value={(b.final_release || {}).checked_by}
              onChange={(v) => setFr("checked_by", v)} readonly={readonly} />
          </div>
        </div>
      </Card>

      {/* Vials, Stoppers & Flip Caps + Disinfection lot capture */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Vials, Stoppers &amp; Flip Caps</h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Recipe defines the type/size — enter this batch's lot # and expiration. Blank fields print underscore lines on the PDF.
        </p>
        {(b.vial_lots || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No vial specs on the parent formula. Add them under the Master Formula to pre-populate this table for future batches.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Linked SKU (Inventory)</TableHead>
                  <TableHead>Lot / Run #</TableHead>
                  <TableHead>Expiration date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(b.vial_lots || []).map((v, i) => (
                  <InventoryLotRow key={i} idx={i} row={v} inventory={inventory}
                    readonly={readonly} onChange={setVialLot} testPrefix="vial-lot"
                    matchTerm={`${v.type || ""} ${v.size || ""}`}
                    label={<><span className="font-medium text-slate-800">{v.type || "—"}</span></>}
                    subLabel={v.size || "—"} showQty={true} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Disinfection &amp; cleaning agents</h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Agents from the recipe with editable lot &amp; expiration. Prints on Page 3 of the batch worksheet.
        </p>
        {(b.disinfection_lots || []).length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No agents declared on the formula — PDF will print Peridox RTU + Sterile IPA 70% with blank underscore lines.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Agent</TableHead>
                  <TableHead>Linked SKU (Inventory)</TableHead>
                  <TableHead>Lot #</TableHead>
                  <TableHead>Expiration date</TableHead>
                  <TableHead>Qty used (auto-drawdown)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(b.disinfection_lots || []).map((a, i) => (
                  <InventoryLotRow key={i} idx={i} row={a} inventory={inventory}
                    readonly={readonly} onChange={setDisinfLot} testPrefix="disinf-lot"
                    matchTerm={a.agent || ""}
                    label={<span className="font-medium text-slate-800">{a.agent || "—"}</span>}
                    subLabel="" showQty={true} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Status history */}
      <Card className="p-4 mb-4 border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Status history</h3>
        <div className="space-y-1">
          {(b.status_history || []).map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-700 py-1 border-b border-slate-100 last:border-b-0">
              <span className="text-[10px] text-slate-500 font-mono w-32">{(h.ts || "").slice(0, 16).replace("T", " ")}</span>
              <StatusBadge status={h.from || "draft"} /> <span>→</span> <StatusBadge status={h.to} />
              <span className="text-slate-500 flex-1 truncate">{h.notes}</span>
              <span className="text-[10px] text-slate-400">{h.actor_email}</span>
            </div>
          ))}
        </div>
      </Card>

      {statusDialog && (
        <StatusTransitionDialog data={statusDialog} batch={b}
          onClose={() => setStatusDialog(null)}
          onDone={async () => { setStatusDialog(null); await reload(); }} />
      )}
    </div>
  );
}

function Field({ label, value, onChange, readonly, type = "text", mono }) {
  return (
    <div>
      <label className="text-[10px] text-slate-500 uppercase">{label}</label>
      <Input value={value ?? ""} onChange={(e) => onChange && onChange(e.target.value)} disabled={readonly}
        type={type} className={`h-9 text-sm ${mono ? "font-mono" : ""} disabled:bg-slate-50`} />
    </div>
  );
}

function IngredientRow({ idx, ing, setIng, inventory, readonly }) {
  const linked = inventory.find((it) => it.id === ing.inventory_id);
  const suggested = useMemo(() => {
    if (linked) return inventory;
    const q = (ing.name || "").toLowerCase();
    if (!q) return inventory;
    const hits = inventory.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.brand_name || "").toLowerCase().includes(q) ||
      (it.generic_name || "").toLowerCase().includes(q));
    return hits.length > 0 ? hits : inventory;
  }, [inventory, linked, ing.name]);

  const pickInventory = (invId) => {
    setIng(idx, "inventory_id", invId);
    // clear lot selection when SKU changes
    setIng(idx, "lot_used", "");
  };
  const pickLot = (lotNum) => setIng(idx, "lot_used", lotNum);

  return (
    <TableRow data-testid={`batch-ing-${idx}`}>
      <TableCell className="font-medium text-slate-800 align-top">
        {ing.name}
        <div className="text-[10px] text-slate-500 font-normal">{ing.unit}</div>
      </TableCell>
      <TableCell className="align-top">
        <div className="flex items-center justify-end gap-1">
          <span className="text-xs text-slate-400 font-mono">{ing.planned_quantity}</span>
          <span className="text-slate-300">→</span>
          <Input data-testid={`batch-ing-actual-${idx}`} type="number" step="any"
            value={ing.actual_quantity ?? ""}
            onChange={(e) => setIng(idx, "actual_quantity", e.target.value === "" ? null : Number(e.target.value))}
            disabled={readonly} className="h-8 text-xs font-mono w-20 text-right" placeholder="—" />
        </div>
      </TableCell>
      <TableCell className="align-top">
        <select data-testid={`batch-ing-sku-${idx}`} value={ing.inventory_id || ""}
          onChange={(e) => pickInventory(e.target.value)} disabled={readonly}
          className="h-8 text-xs px-1.5 rounded-md border border-slate-200 max-w-[280px] disabled:bg-slate-50">
          <option value="">— pick SKU —</option>
          {suggested.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name}{it.ndc ? ` · NDC ${it.ndc}` : ""}{it.manufacturer ? ` · ${it.manufacturer}` : ""}
              {" "}({it.total_quantity} {it.unit})
            </option>
          ))}
        </select>
        {linked && (
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
            {linked.ndc ? `NDC ${linked.ndc}` : ""}
            {linked.manufacturer ? ` · ${linked.manufacturer}` : ""}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top">
        {linked ? (
          <select data-testid={`batch-ing-lot-${idx}`} value={ing.lot_used || ""}
            onChange={(e) => pickLot(e.target.value)} disabled={readonly}
            className="h-8 text-xs px-1.5 rounded-md border border-slate-200 max-w-[240px] disabled:bg-slate-50">
            <option value="">— pick lot —</option>
            {(linked.lots || []).map((l) => (
              <option key={l.id} value={l.lot_number}>
                {l.lot_number || "(no #)"} · exp {l.expiration_date || "—"} · {l.quantity} on hand
              </option>
            ))}
          </select>
        ) : (
          <Input value={ing.lot_used || ""} onChange={(e) => setIng(idx, "lot_used", e.target.value)}
            disabled={readonly} className="h-8 text-xs font-mono w-32" placeholder="lot #" />
        )}
      </TableCell>
      <TableCell className="align-top">
        <Input value={ing.weighed_by || ""} onChange={(e) => setIng(idx, "weighed_by", e.target.value)}
          disabled={readonly} className="h-8 text-xs w-24" placeholder="initials" />
      </TableCell>
    </TableRow>
  );
}

// Shared row for any batch line that needs to bind to an Inventory SKU + lot
// (vials, stoppers, flip caps, disinfection agents, and any future "line items").
// Picking a lot auto-populates lot_number + expiration_date on the row so the
// operator never mistypes.
function InventoryLotRow({ idx, row, inventory, readonly, onChange, testPrefix, matchTerm, label, subLabel, showQty }) {
  const linked = inventory.find((it) => it.id === row.inventory_id);
  const suggested = useMemo(() => {
    const q = (matchTerm || "").toLowerCase().trim();
    if (!q) return inventory;
    const hits = inventory.filter((it) =>
      (it.name || "").toLowerCase().includes(q) ||
      (it.brand_name || "").toLowerCase().includes(q) ||
      (it.generic_name || "").toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q));
    return hits.length > 0 ? hits : inventory;
  }, [inventory, matchTerm]);

  const pickInventory = (invId) => {
    onChange(idx, "inventory_id", invId);
    onChange(idx, "lot_number", "");
    onChange(idx, "expiration_date", "");
  };
  const pickLot = (lotNum) => {
    onChange(idx, "lot_number", lotNum);
    const lot = (linked?.lots || []).find((l) => l.lot_number === lotNum);
    if (lot?.expiration_date) onChange(idx, "expiration_date", lot.expiration_date);
  };

  return (
    <TableRow data-testid={`${testPrefix}-${idx}`}>
      <TableCell className="align-top">{label}</TableCell>
      {subLabel !== "" && subLabel !== undefined && (
        <TableCell className="font-mono text-xs align-top">{subLabel}</TableCell>
      )}
      <TableCell className="align-top">
        <select data-testid={`${testPrefix}-sku-${idx}`} value={row.inventory_id || ""}
          onChange={(e) => pickInventory(e.target.value)} disabled={readonly}
          className="h-8 text-xs px-1.5 rounded-md border border-slate-200 max-w-[260px] disabled:bg-slate-50">
          <option value="">— pick SKU —</option>
          {suggested.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name}{it.ndc ? ` · NDC ${it.ndc}` : ""}{it.manufacturer ? ` · ${it.manufacturer}` : ""}
              {" "}({it.total_quantity} {it.unit})
            </option>
          ))}
        </select>
        {linked && (
          <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
            {linked.ndc ? `NDC ${linked.ndc}` : ""}
            {linked.manufacturer ? ` · ${linked.manufacturer}` : ""}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top">
        {linked ? (
          <select data-testid={`${testPrefix}-num-${idx}`} value={row.lot_number || ""}
            onChange={(e) => pickLot(e.target.value)} disabled={readonly}
            className="h-8 text-xs px-1.5 rounded-md border border-slate-200 max-w-[220px] disabled:bg-slate-50">
            <option value="">— pick lot —</option>
            {(linked.lots || []).map((l) => (
              <option key={l.id} value={l.lot_number}>
                {l.lot_number || "(no #)"} · exp {l.expiration_date || "—"} · {l.quantity} on hand
              </option>
            ))}
          </select>
        ) : (
          <Input value={row.lot_number || ""}
            onChange={(e) => onChange(idx, "lot_number", e.target.value)}
            disabled={readonly} className="h-8 text-xs font-mono w-32" placeholder="lot #"
            data-testid={`${testPrefix}-num-${idx}`} />
        )}
      </TableCell>
      <TableCell className="align-top">
        <Input type="date" value={row.expiration_date || ""}
          onChange={(e) => onChange(idx, "expiration_date", e.target.value)}
          disabled={readonly} className="h-8 text-xs font-mono w-36"
          data-testid={`${testPrefix}-exp-${idx}`} />
      </TableCell>
      {showQty && (
        <TableCell className="align-top">
          <Input type="number" step="any" min="0"
            value={row.quantity_used ?? ""}
            onChange={(e) => onChange(idx, "quantity_used", e.target.value === "" ? null : Number(e.target.value))}
            disabled={readonly} className="h-8 text-xs font-mono w-20"
            placeholder="qty"
            data-testid={`${testPrefix}-qty-${idx}`} />
        </TableCell>
      )}
    </TableRow>
  );
}

function StatusTransitionDialog({ data, batch, onClose, onDone }) {
  const to = data.to;
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const titles = {
    in_progress: { title: "Start compounding", hint: "Stamps the actual start time. Signatures are hand-written on the printed worksheet." },
    post_fill: { title: "Mark filled", hint: "Locks in end time and deducts linked ingredients / vials / disinfection agents from Inventory. Signatures are hand-written." },
    completed: { title: "Complete batch", hint: "Requires visual inspection = Pass. Locks the record read-only. Signatures are hand-written on the printed worksheet." },
  };
  const meta = titles[to] || {};

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/batch-logs/${batch.id}/status`, { to, notes });
      toast.success(`Advanced to ${to.replace("_", " ")}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Transition failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{meta.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">{meta.hint}</p>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Notes</label>
            <Input data-testid="stat-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              className="h-9 text-sm" placeholder="Optional context saved in history" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="stat-confirm" onClick={submit} disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Sign &amp; advance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
